import {
  FaHome,
  FaInfoCircle,
  FaChalkboardTeacher,
  FaGraduationCap,
} from "react-icons/fa";
import type { IconType } from "react-icons";

export interface IMenuItem {
  title?: string;
  name?: string;
  path: string;
  description?: string;
  isExternal?: boolean;
  // Off-pillar/beginner topics: kept in MENU so routes + category pages + SEO keep
  // working, but hidden from the top-nav mega-menu to keep the nav staff/architect-focused.
  // Still indexed and listed on the /learnings "All Topics" page.
  hidden?: boolean;
}

export interface IMenuCategory {
  title: string;
  name?: string;
  icon?: IconType;
  items?: IMenuItem[];
  path?: string;
  description?: string;
  extraClass?: string;
  isExternal?: boolean;
}

export interface IMenu {
  HOME: IMenuCategory;
  VIDEOS: IMenuCategory;
  COURSES?: IMenuCategory;
  REVIEWS: IMenuCategory;
  LEARNING: IMenuCategory;
  ABOUT: IMenuCategory;
  MENTORSHIP: IMenuCategory;
  VENTURES: IMenuCategory;
  SEARCH: IMenuCategory;
}

export const MENU: IMenu = {
  HOME: {
    title: "Home",
    icon: FaHome,
    path: "/",
    name: "home",
    description: "Return to the homepage",
  },
  VIDEOS: {
    title: "Podcast",
    path: "/podcast",
    name: "videos",
    description: "Where I break down system failures, architecture trade-offs, and senior engineering decisions.",
  },
  REVIEWS: {
    title: "Reviews",
    path: "/reviews",
    name: "reviews",
    description: "Honest reviews of books, tools, and resources I've actually used.",
  },
  COURSES: {
    title: "Courses",
    icon: FaGraduationCap,
    path: "/courses",
    name: "courses",
    description: "Explore comprehensive courses on web development and programming.",
  },
  LEARNING: {
    title: "Learnings",
    icon: FaChalkboardTeacher,
    items: [
      {
        title: "HTML and CSS",
        path: "/html-css",
        name: "csses",
        description:
          "The building blocks. Layout, styling, and making things look right.",
        hidden: true,
      },
      {
        title: "TypeScript",
        path: "/typescript",
        name: "type-scripts",
        description:
          "Types, generics, and the patterns that make large codebases manageable.",
      },
      {
        title: "SQL",
        path: "/sql",
        name: "sqls",
        description:
          "Queries, joins, and the SQL you actually need in production.",
      },
      {
        title: "Security",
        path: "/security",
        name: "securities",
        description:
          "Auth, encryption, and the security mistakes I've seen in real systems.",
      },
      {
        title: "Accessibility",
        path: "/accessibility",
        name: "accessibilities",
        description:
          "Building for everyone. ARIA, screen readers, and inclusive UI.",
        hidden: true,
      },
      {
        title: "System Design",
        path: "/system-design",
        name: "system-designs",
        description:
          "The trade-offs behind scalable architecture. CAP theorem, load balancing, caching.",
      },
      {
        title: "Database",
        path: "/database",
        name: "databases",
        description:
          "Indexing, normalization, and keeping your queries fast at scale.",
      },
      {
        title: "Design Pattern",
        path: "/design-pattern",
        name: "design-patterns",
        description:
          "Patterns that show up everywhere once you know what to look for.",
      },
      {
        title: "Clean Code",
        path: "/clean-code",
        name: "clean-codes",
        description:
          "Writing code your future self (and teammates) won't hate reading.",
      },
      {
        title: "Testing",
        path: "/testing",
        name: "testings",
        description:
          "Unit tests, integration tests, and knowing what's actually worth testing.",
        hidden: true,
      },
      {
        title: "DevOps",
        path: "/devops",
        name: "dev-ops",
        description: "CI/CD, containers, and getting your code to production without breaking things.",
      },
      {
        title: "Artificial Intelligence",
        path: "/artificial-intelligence",
        name: "artificial-intelligences",
        description:
          "LLMs, ML pipelines, and using AI as a practical engineering tool.",
      },
      {
        title: "Algorithm",
        path: "/algorithm",
        name: "algorithms",
        description:
          "The fundamentals that come up in interviews and real-world performance tuning.",
        hidden: true,
      },
      {
        title: "Data Structure",
        path: "/data-structure",
        name: "data-structures",
        description:
          "Arrays, trees, graphs, and knowing which one to reach for.",
        hidden: true,
      },
      {
        title: "Leadership",
        path: "/leadership",
        name: "leaderships",
        description:
          "Engineering leadership, career growth, team management, and mentorship.",
      },
    ],
  },
  ABOUT: {
    title: "About",
    icon: FaInfoCircle,
    items: [
      {
        title: "About",
        path: "/about",
        name: "about",
        description: "The full story -- 16+ years of building software, from Tehran to London.",
      },
      {
        title: "Talks",
        path: "/talks",
        name: "talks",
        description: "Conference talks and presentations on the things I've learned the hard way.",
      },
      {
        title: "Testimonials",
        path: "/testimonials",
        name: "testimonials",
        description: "What engineers and mentees say about working with me.",
      },
      {
        title: "Projects",
        path: "/projects",
        name: "projects",
        description:
          "Things I've built -- production systems, open-source tools, and side projects.",
      },
      {
        title: "Ventures",
        path: "/ventures",
        name: "ventures",
        description:
          "ClubCP, Coding and Marlin. The three products I own and still maintain.",
      },
    ],
  },
  MENTORSHIP: {
    title: "Mentorship",
    path: "/mentorship",
    name: "mentorship",
    description:
      "Principal Engineer and team mentor. Scalable systems, career strategy, and hands-on guidance for senior engineers.",
  },
  VENTURES: {
    title: "Ventures",
    path: "/ventures",
    name: "ventures",
    description: "ClubCP, Coding and Marlin. The three products I own and still maintain.",
  },
  SEARCH: {
    title: "Search",
    path: "/search",
    description: "Search everything in gazar.dev",
  },
};
